// src/services/UserService.js

import { supabase, isDemoMode } from '../lib/supabase';
import { authService } from './AuthService';
import { checkAccessCode } from './accessCodeRpc';

// Deterministic referral code from a wallet, e.g. 0xcb93c69d... -> "REF-CB93C69D".
// Pure/sync — no I/O, so not async and not a class method.

// USERS ROWS ARE SERVER-OWNED (Packet 04). The browser only READS users;
// creation happens at sign-in (siwe-verify → provision_user) and every edit
// goes through the SIWE-checked user-profile function, which allows only
// username / display_name / avatar_url / bio of the caller's own row.
async function profileBridge(action, payload = {}) {
  const wallet = authService.getSessionWallet();
  const token = wallet ? authService.getToken(wallet) : null;
  if (!token) {
    return { data: null, error: { message: 'Sign in first', code: 'sign_in_required' } };
  }
  const { data, error } = await supabase.functions.invoke('user-profile', {
    body: { action, ...payload },
    headers: { 'X-Lunexx-Auth': `Bearer ${token}` },
  });
  if (error) {
    let body = null;
    try { body = await error.context?.json?.(); } catch { /* ignore */ }
    const code = body?.error || 'request_failed';
    const message = code === 'username_taken' ? 'Username already taken'
      : code === 'invalid_profile' ? (body?.errors || []).join('; ') || 'Invalid profile'
      : body?.error || error.message || 'Request failed';
    return { data: null, error: { message, code } };
  }
  return { data, error: null };
}
// Capture referral code from URL on page load and store it
(function captureReferralCode() {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const refCode = params.get('ref') || params.get('referral');

  if (refCode) {
    localStorage.setItem('lunexx_referral_code', refCode.toUpperCase());
    console.log('Referral code captured:', refCode);
  }
})();

class UserService {
  /**
   * Validate an access code
   */
  async validateAccessCode(code) {
    if (isDemoMode) {
      return { valid: code === 'DEMO', error: null };
    }

    // Server-side answer only (039-01): access_codes is not readable here.
    const r = await checkAccessCode(code);
    if (r.transportError) {
      return { valid: false, error: r.transportError };
    }
    if (!r.valid) {
      return { valid: false, error: { message: r.message, reason: r.reason } };
    }
    return { valid: true, isReferral: r.isReferral, error: null };
  }

  /**
   * Use an access code (increment usage count)
   */
  async useAccessCode(code) {
    if (isDemoMode) {
      return { success: true, error: null };
    }

    const { error } = await supabase.rpc('increment_access_code_uses', {
      p_code: code.toUpperCase().trim(),
    });

    return { success: !error, error };
  }

  /**
   * Check if username is available
   */
  async isUsernameAvailable(username, excludeUserId = null) {
    if (isDemoMode) return { available: true, error: null };
    const normalized = username.toLowerCase().trim();
    let q = supabase.from('users').select('id').eq('username', normalized);
    if (excludeUserId) q = q.neq('id', excludeUserId);
    const { data } = await q.limit(1);
    return { available: !data?.length, error: null };
  }


  /**
   * Get or create user by wallet address
   * Auto-creates user with just wallet address (username optional)
   * Automatically uses stored referral code if available
   */
  async getOrCreateByWallet(walletAddress, referralCode = null) {
    const wallet = walletAddress.toLowerCase();

    const { data: existing } = await supabase
      .from('users').select('*').eq('wallet_address', wallet).maybeSingle();
    if (existing) return { data: existing, error: null, isNew: false };

    // No row yet: only the server may create it, and only for a wallet that
    // has signed in. Before sign-in there is simply no user (fail closed —
    // never an anonymous insert).
    if (!authService.getToken(wallet)) {
      return { data: null, error: { message: 'Sign in first', code: 'sign_in_required' }, isNew: false, needsSignIn: true };
    }
    const { data, error } = await profileBridge('me', { referralCode: referralCode || undefined });
    if (error || !data?.user) return { data: null, error: error || { message: 'Could not create your account' }, isNew: false };
    return { data: data.user, error: null, isNew: data.created === true };
  }
  /**
   * Set or update username (optional, can be done anytime)
   * @param {string} userIdOrWallet - User UUID or wallet address
   * @param {string} username - New username
   */
  async setUsername(userIdOrWallet, username) {
    if (isDemoMode) {
      return { data: { id: userIdOrWallet, username }, error: null };
    }
    // Always the SIGNED-IN user's own row (the argument is kept for
    // call-site compatibility; the server derives the row from the session).
    const { data, error } = await profileBridge('update', { patch: { username } });
    if (error) return { data: null, error };
    return { data: data.user, error: null };
  }

  /**
   * Update user profile (username and display_name)
   * @param {string} walletAddress - User's wallet address
   * @param {object} updates - { username, display_name }
   */
  async updateProfile(walletAddress, updates) {
    if (isDemoMode) {
      return { data: { wallet_address: walletAddress, ...updates }, error: null };
    }
    const session = authService.getSessionWallet();
    if (walletAddress && session && walletAddress.toLowerCase() !== session) {
      return { data: null, error: { message: 'You can only edit your own profile' } };
    }
    const patch = {};
    for (const k of ['username', 'display_name', 'avatar_url', 'bio']) {
      if (updates[k] !== undefined) patch[k] = updates[k];
    }
    const { data, error } = await profileBridge('update', { patch });
    if (error) return { data: null, error };
    return { data: data.user, error: null };
  }
  /**
   * Get user by wallet address
   */
  async getByWallet(walletAddress) {
    if (isDemoMode) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('wallet_address', walletAddress.toLowerCase())
      .limit(1);

    return { data: data?.[0] || null, error };
  }

  /**
   * Get user by username (case-insensitive). Public lookup — used by the
   * public profile page (/u/:username). Only selects public-safe columns.
   */
  async getByUsername(username) {
    if (isDemoMode) return { data: null, error: null };
    if (!username) return { data: null, error: { message: 'username required' } };

    const { data, error } = await supabase
      .from('users')
      .select('id, username, display_name, avatar_url, bio, wallet_address, created_at')
      .ilike('username', username)
      .limit(1);

    return { data: data?.[0] || null, error };
  }

  /**
   * Get user by ID
   */
  async getById(userId) {
    if (isDemoMode) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    return { data, error };
  }

  /**
   * Update last seen timestamp
   */
  async updateLastSeen(userId) {
    if (isDemoMode) return { error: null };

    const { error } = await profileBridge('touch');
    return { error };
  }

  /**
   * Check free messages remaining for a creator
   */
  async checkFreeMessages(userId, creatorId) {
    if (isDemoMode) {
      return {
        hasFreeMessages: true,
        freeRemaining: 5,
        isSubscribed: false,
        error: null
      };
    }

    const { data, error } = await supabase
      .rpc('check_free_messages', {
        p_user_id: userId,
        p_creator_id: creatorId,
      });

    if (error) {
      console.error('Error checking free messages:', error);
      return { hasFreeMessages: true, freeRemaining: 5, isSubscribed: false, error };
    }

    const result = data?.[0];
    return {
      hasFreeMessages: result?.has_free_messages ?? true,
      freeRemaining: result?.free_remaining ?? 5,
      isSubscribed: result?.is_subscribed ?? false,
      error: null,
    };
  }

  /**
   * Increment message count (after sending a message)
   */
  async incrementMessageCount(userId, creatorId) {
    if (isDemoMode) return { error: null };

    const { error } = await supabase
      .rpc('increment_message_count', {
        p_user_id: userId,
        p_creator_id: creatorId,
      });

    return { error };
  }

  /**
   * Get platform settings
   */
  async getPlatformSettings() {
    if (isDemoMode) {
      return {
        data: {
          message_price_usd: 0.50,
          free_messages_per_creator: 5,
          platform_fee_percent: 15,
        },
        error: null,
      };
    }

    const { data, error } = await supabase
      .from('platform_settings')
      .select('key, value');

    if (error) return { data: null, error };

    // Convert to object
    const settings = data.reduce((acc, { key, value }) => {
      acc[key] = value;
      return acc;
    }, {});

    return { data: settings, error: null };
  }
}

export const userService = new UserService();
export default userService;
